<!--
@component
MatchCard

See markup below.
-->

<script lang="ts">
  import type { Match } from '$lib/@types/opta';
  import { appState } from '$lib/stores/index.svelte';
  import { fly } from 'svelte/transition';
  import { isUpcoming, getAwayTeam, getHomeTeam } from '$utils/getMatchStats';
  import getMatchPath from '$utils/getMatchPath';

  import MatchResult from './MatchResult.svelte';
  import MatchSked from './MatchSked.svelte';

  type Props = {
    match: Match;
    i?: number;
    flagLink?: boolean;
    linked?: boolean;
    sticky?: boolean;
    live?: boolean;
  };

  let {
    match,
    i = 0,
    flagLink = false,
    linked = false,
    sticky = false,
    live = $bindable(),
  }: Props = $props();

  const futureMatch = $derived(!!isUpcoming(match));
  const hasTeams = $derived(!!getHomeTeam(match) && !!getAwayTeam(match));
  // Defer to `getMatchPath` so the embed branch (`appState.embed`)
  // flows through automatically. `match.path` is decorated by
  // `getData()` (#37); `'.'` indicates only one contestant is known,
  // in which case `getMatchPath` returns `{ url: '.' }` and we skip
  // wrapping the card in a link.
  const matchPath = $derived(getMatchPath(match));
  const matchUrl = $derived(
    matchPath && matchPath.url !== '.' ? matchPath.url : undefined
  );
  const wrap = $derived(linked && hasTeams && !!matchUrl);
</script>

{#snippet card()}
  <div
    class="match mx-auto"
    class:live
    class:loading={appState.isLoading}
    class:sticky
    transition:fly|local={{ y: 50, duration: 200, delay: i * 20 }}
  >
    {#if futureMatch}
      <MatchSked {match} {flagLink} />
    {:else}
      <MatchResult
        isFixture={futureMatch}
        bind:live
        {flagLink}
        {match}
        liveData={match.liveData}
      />
    {/if}
  </div>
{/snippet}

{#if wrap}
  <a
    href={matchUrl}
    target={appState.embed ? '_top' : undefined}
    class:loading={appState.isLoading}
  >
    {@render card()}
  </a>
{:else}
  {@render card()}
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  a {
    margin: auto;
    max-width: 35rem;
    text-decoration: inherit;
    color: inherit;
    display: block;
    border-radius: var(--theme-style-border-radius);

    &.loading {
      pointer-events: none;
      cursor: default;
    }

    @include mixins.hover-supported() {
      overflow: visible;
      opacity: 0.9;
      text-decoration: inherit;
      color: inherit;
    }
  }

  .match:not(.showKit) {
    max-width: 35rem;
  }

  .match {
    position: relative;

    &.loading,
    &.live {
      &:before {
        content: '';
        position: absolute;
        z-index: 1;
        left: 0;
        top: 0;
        border-radius: var(--theme-style-border-radius);
        mix-blend-mode: multiply;
        width: 100%;
        height: 100%;
        @include mixins.loading;
      }
    }
  }
</style>
